/**
 * Property specs and event builders of the registry.
 *
 * Pure TypeScript with no runtime dependency: the root Next app, jest, tsx
 * scripts and the Vite apps all load this file. Never import posthog-js, read
 * `window` or `import.meta` here.
 */

import type { App } from './apps';

export type PropSpec =
  | { readonly kind: 'string'; readonly max?: number }
  | { readonly kind: 'number' }
  | { readonly kind: 'boolean' }
  | { readonly kind: 'enum'; readonly values: readonly string[] }
  | { readonly kind: 'record'; readonly of: PropSpec }
  | { readonly kind: 'array'; readonly of: PropSpec }
  | { readonly kind: 'nullable'; readonly of: PropSpec };

type StringSpec = { readonly kind: 'string'; readonly max?: number };
type NumberSpec = { readonly kind: 'number' };
type BooleanSpec = { readonly kind: 'boolean' };
type EnumSpec<T extends string> = { readonly kind: 'enum'; readonly values: readonly T[] };
type RecordSpec<S extends PropSpec> = { readonly kind: 'record'; readonly of: S };
type ArraySpec<S extends PropSpec> = { readonly kind: 'array'; readonly of: S };
type NullableSpec<S extends PropSpec> = { readonly kind: 'nullable'; readonly of: S };

export const p = {
  string: (max?: number): StringSpec => (max === undefined ? { kind: 'string' } : { kind: 'string', max }),
  number: (): NumberSpec => ({ kind: 'number' }),
  boolean: (): BooleanSpec => ({ kind: 'boolean' }),
  enum: <T extends string>(values: readonly T[]): EnumSpec<T> => ({ kind: 'enum', values }),
  record: <S extends PropSpec>(of: S): RecordSpec<S> => ({ kind: 'record', of }),
  array: <S extends PropSpec>(of: S): ArraySpec<S> => ({ kind: 'array', of }),
  nullable: <S extends PropSpec>(of: S): NullableSpec<S> => ({ kind: 'nullable', of }),
};

/** The TypeScript value type of one spec. */
export type PropValue<S> =
  S extends NullableSpec<infer I>
    ? PropValue<I> | null
    : S extends StringSpec
      ? string
      : S extends NumberSpec
        ? number
        : S extends BooleanSpec
          ? boolean
          : S extends EnumSpec<infer T>
            ? T
            : S extends RecordSpec<infer I>
              ? Record<string, PropValue<I>>
              : S extends ArraySpec<infer I>
                ? PropValue<I>[]
                : never;

type NullableKeys<P> = { [K in keyof P]: P[K] extends { kind: 'nullable' } ? K : never }[keyof P];
type RequiredKeys<P> = Exclude<keyof P, NullableKeys<P>>;
type Flatten<T> = { [K in keyof T]: T[K] };

/**
 * Props object of an event. Nullable props are optional: a missing key and
 * `null` mean the same thing in PostHog, and callers should not have to spell
 * out every unknown value.
 */
export type PropsOf<P> = Flatten<
  { [K in RequiredKeys<P>]: PropValue<P[K]> } & { [K in NullableKeys<P>]?: PropValue<P[K]> }
>;

export type EventSource = 'client' | 'server';
export type EventStatus = 'active' | 'legacy' | 'external';

export interface EventDef<
  N extends string = string,
  P extends Record<string, PropSpec> = Record<string, PropSpec>,
  S extends EventSource = EventSource,
> {
  readonly name: N;
  readonly description: string;
  readonly source: S;
  readonly apps: readonly App[];
  readonly props: P;
  readonly owner?: string;
  readonly status: EventStatus;
  /** Set by `defineWorkflow`/`defineOutcome`: the `<area>:<stem>` the event belongs to. */
  readonly workflow?: string;
}

export interface EventInput<P extends Record<string, PropSpec>, S extends EventSource = EventSource> {
  description: string;
  source: S;
  apps: readonly App[];
  props: P;
  owner?: string;
  status?: EventStatus;
}

export function defineEvent<
  const N extends string,
  const P extends Record<string, PropSpec>,
  const S extends EventSource,
>(name: N, def: EventInput<P, S>): EventDef<N, P, S> {
  return {
    name,
    description: def.description,
    source: def.source,
    apps: def.apps,
    props: def.props,
    ...(def.owner ? { owner: def.owner } : {}),
    status: def.status ?? 'active',
  };
}

export const ERROR_KINDS = [
  'validation',
  'permission',
  'not_found',
  'conflict',
  'precondition',
  'rate_limit',
  'provider',
  'network',
  'timeout',
  'server',
  'unknown',
] as const;
export type ErrorKind = (typeof ERROR_KINDS)[number];

/** The triad every workflow `_fail` carries (A2). */
export const FAIL_PROPS = {
  error_kind: p.enum(ERROR_KINDS),
  error_code: p.string(64),
  request_id: p.nullable(p.string(64)),
} as const;
type FailProps = typeof FAIL_PROPS;

type Props = Record<string, PropSpec>;
type None = Record<never, PropSpec>;

export interface Side<X extends Props, S extends EventSource> {
  source: S;
  apps: readonly App[];
  /** Extra props of this event only, on top of the shared `props`. */
  props?: X;
  description?: string;
}

function sideEvent<N extends string, P extends Props, S extends EventSource>(
  name: N,
  workflow: string,
  side: Side<Props, S>,
  description: string,
  props: P,
  owner: string | undefined,
): EventDef<N, P, S> {
  return {
    ...defineEvent(name, {
      description: side.description ?? description,
      source: side.source,
      apps: side.apps,
      props,
      ...(owner ? { owner } : {}),
    }),
    workflow,
  };
}

/**
 * A success/failure pair without a start event: `<area>:<stem>` and
 * `<area>:<stem>_fail`. The fail event adds `error_kind`, `error_code` and
 * `request_id`. Each side declares its own source (the server middleware emits
 * most pairs; `onboarding:account_create_fail` is client-side).
 */
export function defineOutcome<
  const A extends string,
  const St extends string,
  const P extends Props,
  const SS extends EventSource,
  const FS extends EventSource,
  const SX extends Props = None,
  const FX extends Props = None,
>(
  area: A,
  stem: St,
  def: { description: string; props: P; success: Side<SX, SS>; fail: Side<FX, FS>; owner?: string },
) {
  const workflow = `${area}:${stem}` as const;
  const success = sideEvent(
    workflow,
    workflow,
    def.success,
    def.description,
    { ...def.props, ...(def.success.props ?? ({} as SX)) } as P & SX,
    def.owner,
  );
  const fail = sideEvent(
    `${workflow}_fail` as const,
    workflow,
    def.fail,
    `${def.description} Failed.`,
    { ...def.props, ...FAIL_PROPS, ...(def.fail.props ?? ({} as FX)) } as P & FailProps & FX,
    def.owner,
  );
  return [success, fail] as const;
}

/**
 * The workflow triplet of A2: `<area>:<stem>_start` (the user committed),
 * `<area>:<stem>` (success) and `<area>:<stem>_fail` (failure, with
 * `error_kind`, `error_code`, `request_id`). The stem's last token is a verb.
 */
export function defineWorkflow<
  const A extends string,
  const St extends string,
  const P extends Props,
  const TS extends EventSource,
  const SS extends EventSource,
  const FS extends EventSource,
  const TX extends Props = None,
  const SX extends Props = None,
  const FX extends Props = None,
>(
  area: A,
  stem: St,
  def: {
    description: string;
    props: P;
    start: Side<TX, TS>;
    success: Side<SX, SS>;
    fail: Side<FX, FS>;
    owner?: string;
  },
) {
  const workflow = `${area}:${stem}` as const;
  const start = sideEvent(
    `${workflow}_start` as const,
    workflow,
    def.start,
    `${def.description} Started.`,
    { ...def.props, ...(def.start.props ?? ({} as TX)) } as P & TX,
    def.owner,
  );
  const [success, fail] = defineOutcome<A, St, P, SS, FS, SX, FX>(area, stem, def);
  return [start, success, fail] as const;
}
